// MAIN-849: a small, dedicated edit form for one staged import proposal.
// Deliberately NOT the AddMenu.js modal — that's tightly coupled to
// CREATE_MENU/UPDATE_MENU and image upload (MAIN-848, explicitly out of
// scope here). Editing here only ever writes to the staged proposal via
// reviewMenuImportProposal — it never touches a live Menu document.
import React, { useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { getEffectiveProposalFields } from "./menuImportReview.utils";

const DIETARY_OPTIONS = [
  "Vegetarian",
  "Non-Vegetarian",
  "Gluten Free",
  "Veg and Gluten Free",
  "Non-Veg and Gluten Free",
  "None",
];

export default function MenuImportProposalEditModal({
  proposal,
  categories,
  onCancel,
  onSave,
  saving,
  focusField = null,
}) {
  // MAIN-879: opened from a Fix button, the editor lands on the field that
  // needs attention and marks it. autoFocus alone loses to the Dialog's own
  // focus trap, so the field is focused once the dialog has finished opening.
  const fieldRefs = useRef({});
  const focusProps = (name) => ({
    ref: (node) => {
      fieldRefs.current[name] = node;
    },
    ...(focusField === name ? { color: "warning", focused: true } : {}),
  });
  const focusTargetField = () => {
    const root = fieldRefs.current[focusField];
    const target = root?.querySelector?.('input, textarea, [role="combobox"]') || root;
    if (target?.focus) target.focus();
  };
  // MAIN-849: editedFields arrives with every field present and unedited ones
  // null, so spreading it over the proposal blanked those fields on a second
  // edit. The effective values are what the card shows and what publishes.
  const current = getEffectiveProposalFields(proposal);
  const [itemName, setItemName] = useState(current.itemName || "");
  const [price, setPrice] = useState(current.price ?? "");
  const [categoryId, setCategoryId] = useState(
    current.categoryId || proposal?.matchedMenuItem?.categoryId || "",
  );
  const [itemDescription, setItemDescription] = useState(
    current.itemDescription || "",
  );
  const [dietory, setDietory] = useState(current.dietory || "");
  const [itemPkgType, setItemPkgType] = useState(current.itemPkgType || "");
  const [minQuantity, setMinQuantity] = useState(current.minQuantity ?? "");

  if (!proposal) return null;

  function handleSave() {
    const editedFields = {};
    if (itemName.trim()) editedFields.itemName = itemName.trim();
    if (price !== "") editedFields.price = Number(price);
    if (categoryId) editedFields.categoryId = categoryId;
    if (itemDescription.trim()) editedFields.itemDescription = itemDescription.trim();
    if (dietory) editedFields.dietory = dietory;
    if (itemPkgType.trim()) editedFields.itemPkgType = itemPkgType.trim();
    if (minQuantity !== "") editedFields.minQuantity = Number(minQuantity);
    onSave(editedFields);
  }

  return (
    <Dialog
      open
      onClose={onCancel}
      fullWidth
      maxWidth="sm"
      TransitionProps={{ onEntered: focusTargetField }}>
      <DialogTitle>Edit {current.itemName || "item"}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          margin="normal"
          label="Item name"
          {...focusProps("itemName")}
          value={itemName}
          onChange={(e) => setItemName(e.target.value)}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Price"
          type="number"
          {...focusProps("price")}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <FormControl
          fullWidth
          margin="normal"
          {...(focusField === "categoryId" ? { color: "warning", focused: true } : {})}>
          <InputLabel id="edit-proposal-category-label">Category</InputLabel>
          <Select
            labelId="edit-proposal-category-label"
            label="Category"
            ref={(node) => {
              fieldRefs.current.categoryId = node;
            }}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}>
            {(categories || []).map((category) => (
              <MenuItem key={category._id} value={category._id}>
                {category.categoryTitle}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="edit-proposal-dietary-label">Dietary</InputLabel>
          <Select
            labelId="edit-proposal-dietary-label"
            label="Dietary"
            value={dietory}
            onChange={(e) => setDietory(e.target.value)}>
            {DIETARY_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          margin="normal"
          label="Serving / portion"
          placeholder="e.g. Tray, serves 10"
          value={itemPkgType}
          onChange={(e) => setItemPkgType(e.target.value)}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Minimum quantity"
          type="number"
          {...focusProps("minQuantity")}
          value={minQuantity}
          onChange={(e) => setMinQuantity(e.target.value)}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Description"
          multiline
          minRows={2}
          value={itemDescription}
          onChange={(e) => setItemDescription(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
